import Cookies from 'js-cookie';
import { queryClient } from '../App';
import { clearPatchLogDeferralFlags } from '@/features/patch-logs/deferral-storage';
import { SESSION_CLEARED_EVENT, SESSION_STARTED_EVENT } from './session-events';

export type StoredAuthData = {
  id?: number | string;
  email: string;
  name?: string;
  ppjkNpwp?: string;
  authSource?: string;
  externalProvider?: string;
  externalSubject?: string;
  source?: string;
  isCCNUser?: boolean;
  companyName?: string;
  companyId?: string;
};

const isSecureCookie = () => typeof window !== 'undefined' && window.location.protocol === 'https:';

export const storeJarvisTokens = (accessToken: string, refreshToken: string) => {
  Cookies.set('accessToken', accessToken, { secure: isSecureCookie(), sameSite: 'strict' });
  Cookies.set('refreshToken', refreshToken, { secure: isSecureCookie(), sameSite: 'strict' });
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
};

// Single choke point for `nomor_ppjk`. Every auth path funnels through here,
// so the warning below is the one to watch when a session comes up without its
// PPJK NPWP — it means the caller had no ppjkNpwp to store.
export const storeAuthData = (authData: StoredAuthData) => {
  localStorage.setItem('authData', JSON.stringify(authData));

  if (authData.ppjkNpwp) {
    localStorage.setItem('nomor_ppjk', authData.ppjkNpwp);
  } else {
    localStorage.removeItem('nomor_ppjk');
    console.warn('[SESSION] nomor_ppjk cleared: auth payload had no ppjkNpwp');
  }
};

/**
 * The signed-in user's email, or undefined when the session has none.
 *
 * Sent with manifest extraction requests so the Connect monitoring card can name who
 * started a run. Without it the card reads "Unknown User" for everyone, which is what it
 * did until 2026-09-09.
 */
export const getSessionEmail = (): string | undefined => {
  try {
    const raw = localStorage.getItem('authData');
    if (!raw) return undefined;
    const email = (JSON.parse(raw) as StoredAuthData)?.email;
    return email?.trim() || undefined;
  } catch {
    return undefined; // a corrupt session must never block an extraction
  }
};

export const clearJarvisSession = () => {
  Cookies.remove('accessToken');
  Cookies.remove('refreshToken');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('authData');
  localStorage.removeItem('nomor_ppjk');
  localStorage.removeItem('mfaEnabled');
  // "Remind me later" is per login; a fresh login must see the modal again.
  clearPatchLogDeferralFlags();
  // Text size is per user: a shared PC must not hand the next person the
  // previous user's size, before or after they sign in.
  localStorage.removeItem('jarvis-text-scale');
  if (typeof document !== 'undefined') {
    document.documentElement.style.fontSize = '';
    document.documentElement.removeAttribute('data-text-scale');
  }
  queryClient.clear();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(SESSION_CLEARED_EVENT));
  }
};

export const completeAuthenticatedSession = ({
  accessToken,
  refreshToken,
  authData,
  mfaEnabled = true,
}: {
  accessToken: string;
  refreshToken: string;
  authData: StoredAuthData;
  mfaEnabled?: boolean;
}) => {
  queryClient.clear();
  storeJarvisTokens(accessToken, refreshToken);
  storeAuthData(authData);
  localStorage.setItem('mfaEnabled', mfaEnabled ? 'true' : 'false');
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(SESSION_STARTED_EVENT));
  }
};

type MasterUserPayload = {
  username?: string | null;
  name?: string | null;
  npwp?: string | null;
};

export const hydrateSessionFromMasterUser = async () => {
  try {
    const accessToken =
      Cookies.get('accessToken') || (typeof window !== 'undefined' ? window.localStorage.getItem('accessToken') : null);

    if (!accessToken || typeof window === 'undefined') {
      console.warn('[SESSION] Hydration skipped: no access token');
      return;
    }

    const response = await fetch('/api/v1/master-ceisa/user', {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    const payload = await response.json().catch(() => null);
    const masterUser = (payload?.success ? payload.data : null) as MasterUserPayload | null;

    if (!masterUser) {
      console.warn('[SESSION] Hydration skipped: /master-ceisa/user returned no user', response.status);
      return;
    }

    const authDataRaw = window.localStorage.getItem('authData');
    if (!authDataRaw) {
      console.warn('[SESSION] Hydration skipped: no authData in localStorage');
      return;
    }

    const authData = JSON.parse(authDataRaw) as StoredAuthData;
    const mergedAuthData: StoredAuthData = {
      ...authData,
      name: masterUser.name || authData.name,
      ppjkNpwp: masterUser.npwp || authData.ppjkNpwp,
    };

    storeAuthData(mergedAuthData);
  } catch (error) {
    // Best-effort hydration only. Login should still succeed without this.
    console.warn('[SESSION] Hydration failed (non-fatal):', error);
  }
};
